import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapPin, Tag, ArrowLeft, ArrowRight, Check } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { CASE_STUDIES } from "@/data/caseStudies";

export default function CaseStudy() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const project = CASE_STUDIES.find((c) => c.slug === slug);

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [slug]);

  const goHome = (hash) => (e) => {
    e.preventDefault();
    navigate("/");
    setTimeout(() => {
      const el = document.querySelector(hash);
      if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
      else el?.scrollIntoView({ behavior: "smooth" });
    }, 150);
  };

  if (!project) {
    return (
      <main className="min-h-screen bg-[#050B14]">
        <Nav />
        <div className="mx-auto max-w-3xl px-5 py-40 text-center">
          <h1 className="font-serif text-4xl uppercase text-white">Project Not Found</h1>
          <Link to="/" data-testid="case-study-not-found-back" className="mt-8 inline-block border border-[#C5A059] px-8 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-[#C5A059]">
            Back to Home
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#050B14]">
      <Nav />

      <section data-testid="case-study-hero" className="relative flex min-h-[70vh] items-end overflow-hidden">
        <img src={project.hero.url} alt={project.hero.alt} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050B14]/70 via-[#050B14]/30 to-[#050B14]" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-16 pt-40 sm:px-8">
          <Reveal>
            <a
              href="/#projects"
              data-testid="case-study-back-link"
              onClick={goHome("#projects")}
              className="mb-8 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-300 transition-colors hover:text-[#C5A059]"
            >
              <ArrowLeft size={14} /> All Projects
            </a>
            <div className="flex flex-wrap items-center gap-4 text-[11px] font-semibold uppercase tracking-[0.3em] text-[#C5A059]">
              <span className="flex items-center gap-1.5"><Tag size={12} /> {project.category}</span>
              <span className="h-1 w-1 rotate-45 bg-[#C5A059]" />
              <span className="flex items-center gap-1.5"><MapPin size={12} /> {project.location}</span>
            </div>
            <h1 className="mt-4 font-serif text-4xl font-light uppercase leading-tight tracking-tight text-white sm:text-6xl">
              {project.title}
            </h1>
            <p className="mt-3 font-serif text-xl italic text-slate-300">{project.subtitle}</p>
          </Reveal>
        </div>
      </section>

      <section data-testid="case-study-overview" className="bg-[#050B14] py-20 sm:py-24">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <div className="mb-6 flex items-center gap-4">
              <span className="h-px w-12 bg-gradient-to-r from-[#C5A059] to-transparent" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.35em] text-[#C5A059]">Overview</span>
            </div>
            <p className="text-base font-light leading-relaxed text-slate-300 sm:text-lg">{project.overview}</p>
            <h2 className="mt-12 font-serif text-2xl font-light uppercase tracking-wide text-white">Scope of Works</h2>
            <ul className="mt-6 space-y-3">
              {project.scope.map((s) => (
                <li key={s} className="flex items-start gap-3 text-sm font-light text-slate-400">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rotate-45 bg-[#C5A059]" />
                  {s}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="border border-[#C5A059]/25 bg-[#0E1726]/70 p-8 sm:p-10">
              <span className="text-[11px] font-semibold uppercase tracking-[0.35em] text-[#C5A059]">Client Outcomes</span>
              <ul className="mt-7 space-y-5">
                {project.outcomes.map((o) => (
                  <li key={o} className="flex items-start gap-4">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center border border-[#C5A059]/40">
                      <Check size={13} className="text-[#C5A059]" />
                    </span>
                    <span className="text-sm font-light leading-relaxed text-slate-200">{o}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="case-study-gallery" className="bg-[#0A111E] py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="mb-12">
            <div className="mb-6 flex items-center gap-4">
              <span className="h-px w-12 bg-gradient-to-r from-[#C5A059] to-transparent" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.35em] text-[#C5A059]">Project Gallery</span>
            </div>
            <h2 className="font-serif text-3xl font-light uppercase tracking-tight text-white sm:text-4xl">
              The Work <span className="gold-text-gradient">in Detail.</span>
            </h2>
          </Reveal>
          <div className="grid gap-5 sm:grid-cols-2">
            {project.gallery.map((g, i) => (
              <Reveal key={g.label} delay={(i % 2) * 0.12}>
                <figure
                  data-testid={`gallery-item-${g.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group relative aspect-[4/3] overflow-hidden border border-[#C5A059]/15"
                >
                  <img
                    src={g.url}
                    alt={g.alt}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050B14]/85 via-transparent to-transparent" />
                  <figcaption className="absolute bottom-4 left-4 border border-[#C5A059]/30 bg-[#050B14]/80 px-3.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.25em] text-[#C5A059] backdrop-blur-sm">
                    {g.label}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="case-study-cta" className="border-t border-[#C5A059]/15 bg-[#050B14] py-20 text-center">
        <Reveal>
          <h2 className="font-serif text-3xl font-light uppercase tracking-tight text-white sm:text-4xl">
            Planning Something <span className="gold-text-gradient">Similar?</span>
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base font-light text-slate-400">
            Tell us about your property and we'll help you plan the next step.
          </p>
          <div className="mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a
              href="/#contact"
              data-testid="case-study-quote-button"
              onClick={goHome("#contact")}
              className="group inline-flex items-center gap-3 bg-[#C5A059] px-9 py-4 text-xs font-bold uppercase tracking-[0.25em] text-[#050B14] transition-all duration-300 hover:bg-[#E6CA65]"
            >
              Request a Quote
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </div>
        </Reveal>
      </section>

      <Footer />
    </main>
  );
}
